import React from 'react';
import { MapPin } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { US_STATES } from '../../lib/constants';
import { toast } from 'sonner';
import AddressValidator from '../../components/AddressValidator';

/** Shipping/billing address card on the Checkout page.
 *
 *  Encapsulates the edit-toggle, the form, the USPS validation widget, and
 *  the "Copy from shipping" shortcut (billing only) that were previously a
 *  220-line `renderAddrCard` helper inside CheckoutPage.
 */
export default function AddressCard({
  type, label, address, isEditing, addrForm,
  setAddrForm, onEdit, onCancel, onSave, userEmail, shippingForCopy,
}) {
  const isBilling = type === 'billing';
  const name = [address?.first_name, address?.last_name].filter(Boolean).join(' ');

  const setField = (key) => (e) =>
    setAddrForm((p) => ({ ...p, [key]: e.target.value }));

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <MapPin className="h-4 w-4" /> {label}
          </CardTitle>
          {!isEditing && (
            <Button
              size="sm" variant="ghost"
              onClick={onEdit}
              data-testid={`edit-${type}-checkout`}
            >Edit</Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <EditForm
            type={type} addrForm={addrForm} setAddrForm={setAddrForm}
            isBilling={isBilling} userEmail={userEmail}
            shippingForCopy={shippingForCopy}
            setField={setField}
            onSave={onSave} onCancel={onCancel}
          />
        ) : address?.address ? (
          <Display address={address} name={name} isBilling={isBilling} />
        ) : (
          <p className="text-sm text-muted-foreground italic">
            No address set — click Edit to add
          </p>
        )}
      </CardContent>
    </Card>
  );
}


function Display({ address, name, isBilling }) {
  return (
    <div className="text-sm space-y-0.5 font-body">
      {name && <p className="font-medium text-foreground">{name}</p>}
      {address.company && <p className="text-muted-foreground">{address.company}</p>}
      <p className="text-foreground">{address.address}</p>
      {address.address_line_2 && <p className="text-foreground">{address.address_line_2}</p>}
      <p className="text-foreground">
        {[address.city, address.state, address.postal_code].filter(Boolean).join(', ')}
      </p>
      {isBilling && address.phone && <p className="text-muted-foreground">Phone: {address.phone}</p>}
      {isBilling && address.email && <p className="text-muted-foreground">Email: {address.email}</p>}
    </div>
  );
}


function EditForm({
  type, addrForm, setAddrForm, isBilling, userEmail, shippingForCopy,
  setField, onSave, onCancel,
}) {
  const copyFromShipping = () => {
    const ship = shippingForCopy || {};
    setAddrForm((prev) => ({
      phone: prev.phone || '',
      email: prev.email || userEmail || '',
      first_name: ship.first_name || '',
      last_name: ship.last_name || '',
      company: ship.company || '',
      address: ship.address || '',
      address_line_2: ship.address_line_2 || '',
      city: ship.city || '',
      state: ship.state || '',
      postal_code: ship.postal_code || '',
    }));
    toast.success('Copied from shipping address');
  };

  return (
    <div className="space-y-2" data-testid={`${type}-form-checkout`}>
      {isBilling && shippingForCopy?.address && (
        <Button
          type="button" size="sm" variant="outline" onClick={copyFromShipping}
          className="w-full justify-center text-xs h-8 border-dashed"
          data-testid="billing-copy-from-shipping-checkout"
        >
          Copy from shipping address
        </Button>
      )}
      <div className="grid grid-cols-2 gap-2">
        <NamedInput type={type} label="First Name *" testid="first-name" name="first_name" value={addrForm.first_name} onChange={setField('first_name')} />
        <NamedInput type={type} label="Last Name *" testid="last-name" name="last_name" value={addrForm.last_name} onChange={setField('last_name')} />
      </div>
      <NamedInput type={type} label="Company Name (Optional)" testid="company" name="company" value={addrForm.company} onChange={setField('company')} />
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground font-body">Street Address *</Label>
        <Input name="address" placeholder="1234 North 5th Street" value={addrForm.address || ''} onChange={setField('address')} data-testid={`${type}-address-checkout`} />
        <Input name="address_line_2" placeholder="Apartment, Suite, etc. (Optional)" value={addrForm.address_line_2 || ''} onChange={setField('address_line_2')} data-testid={`${type}-address2-checkout`} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <NamedInput type={type} label="City *" testid="city" name="city" value={addrForm.city} onChange={setField('city')} />
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground font-body">State *</Label>
          <Select value={addrForm.state || ''} onValueChange={(v) => setAddrForm((p) => ({ ...p, state: v }))}>
            <SelectTrigger data-testid={`${type}-state-checkout`}><SelectValue placeholder="Select state" /></SelectTrigger>
            <SelectContent className="max-h-64">
              {US_STATES.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <NamedInput type={type} label="Zip Code *" testid="zip" name="postal_code" value={addrForm.postal_code} onChange={setField('postal_code')} />
      {addrForm.address && addrForm.city && addrForm.state && (
        <UspsBlock type={type} addrForm={addrForm} setAddrForm={setAddrForm} />
      )}
      {isBilling && (
        <>
          <NamedInput type="" label="Phone Number *" testid="billing-phone" name="phone" inputType="tel" value={addrForm.phone} onChange={setField('phone')} />
          <NamedInput type="" label="Email Address *" testid="billing-email" name="email" inputType="email" value={addrForm.email} onChange={setField('email')} />
        </>
      )}
      <div className="flex gap-2 pt-1">
        <Button size="sm" onClick={onSave} className="bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white" data-testid={`save-${type}-checkout`}>
          Save
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}


function NamedInput({ type, label, testid, name, value, onChange, inputType = 'text' }) {
  // testid prefix handles the few cases where the testid doesn't follow the
  // `${type}-${field}-checkout` pattern (billing-phone/email).
  const dataTestId = type ? `${type}-${testid}-checkout` : `${testid}-checkout`;
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground font-body">{label}</Label>
      <Input name={name} type={inputType} value={value || ''} onChange={onChange} data-testid={dataTestId} />
    </div>
  );
}


function UspsBlock({ type, addrForm, setAddrForm }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid={`${type}-address-validator-checkout`}>
      <p className="text-[11px] font-medium text-slate-700 font-body mb-2">USPS Address Verification</p>
      <AddressValidator
        address={addrForm.address || ''}
        address_line_2={addrForm.address_line_2 || ''}
        city={addrForm.city || ''}
        state={addrForm.state || ''}
        postal_code={addrForm.postal_code || ''}
        onAccept={(std) => {
          setAddrForm((prev) => ({
            ...prev,
            address: std.address || prev.address,
            address_line_2: std.address_line_2 || prev.address_line_2,
            city: std.city || prev.city,
            state: std.state || prev.state,
            postal_code: std.postal_code || prev.postal_code,
          }));
        }}
        triggerLabel="Verify with USPS"
      />
    </div>
  );
}
